import { useCallback, useEffect, useState,Typography  } from 'react';
import { Button, message, Modal, Space, Spin, Switch, Tabs } from 'antd';
import { HolderOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import ParamListView from '../../../components/ParamListView';
import {
  getAllRoles,
  getRoleModuleFields,
  updateRoleModuleFieldConfig,
} from '../../../services/adminApi';
import '../../../styles/UserSettingsSection.css';

// const { Text } = Typography;

const MODULE_TABS = [
  { key: 'job',         label: 'Jobs'        },
  { key: 'candidate',   label: 'Candidates'  },
  { key: 'submissions', label: 'Submissions' },
];

const CONFIG_TYPES = [
  { key: 'listView', label: 'List View' },
  { key: 'filter',   label: 'Filter'    },
  { key: 'form',     label: 'Form'      },
];

function FieldRow({ field, index, onToggle, onReorder }) {
  const dragIdx = useRef(null);
  const [over, setOver]         = useState(false);
  const [dragging, setDragging] = useState(false);

  function handleDragStart(e) { dragIdx.current = index; e.dataTransfer.effectAllowed = 'move'; setDragging(true); }
  function handleDragEnd()    { setDragging(false); setOver(false); }
  function handleDragOver(e)  { e.preventDefault(); setOver(true); }
  function handleDragLeave()  { setOver(false); }
  function handleDrop(e) {
    e.preventDefault(); setOver(false);
    if (dragIdx.current !== null && dragIdx.current !== index) onReorder(dragIdx.current, index);
    dragIdx.current = null;
  }

  return (
    <div
      draggable
      onDragStart={handleDragStart} onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}   onDragLeave={handleDragLeave} onDrop={handleDrop}
      className={`uss-field-row with-edit${over ? ' drag-over' : ''}${dragging ? ' dragging' : ''}`}
    >
      <span className="uss-drag-handle"><HolderOutlined /></span>
      <span className="uss-field-name">
        {field.fieldName}
        <span className="uss-field-key">{field.fieldKey}</span>
      </span>
      <span className="uss-toggle-cell">
        <Switch size="small" checked={field.isVisible}       onChange={(v) => onToggle(index, 'isVisible', v)} />
      </span>
      <span className="uss-toggle-cell">
        <Switch size="small" checked={Boolean(field.isEditable)}  onChange={(v) => onToggle(index, 'isEditable', v)} />
      </span>
      <span className="uss-toggle-cell">
        <Switch size="small" checked={Boolean(field.ismandatory)} onChange={(v) => onToggle(index, 'ismandatory', v)} />
      </span>
    </div>
  );
}

function ModuleFieldConfig({ role, configType }) {
  const [moduleFields, setModuleFields] = useState({});
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState({});
  const [activeModule, setActiveModule] = useState('job');

  useEffect(() => {
    if (!role) return;
    setLoading(true);
    getRoleModuleFields(role, configType)
      .then(setModuleFields)
      .catch(() => message.error('Failed to load field config'))
      .finally(() => setLoading(false));
  }, [role, configType]);

  const fields = moduleFields[activeModule] ?? [];

  function setFields(module, updater) {
    setModuleFields((prev) => ({
      ...prev,
      [module]: typeof updater === 'function' ? updater(prev[module] ?? []) : updater,
    }));
  }

  function handleToggle(idx, key, val) {
    setFields(activeModule, (prev) => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f));
  }

  function handleReorder(from, to) {
    setFields(activeModule, (prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr.map((f, i) => ({ ...f, order: i }));
    });
  }

  async function handleSave() {
    const mod = activeModule;
    setSaving((p) => ({ ...p, [mod]: true }));
    try {
      await updateRoleModuleFieldConfig(role, mod, fields, configType);
      message.success('Saved');
    } catch {
      message.error('Save failed');
    } finally {
      setSaving((p) => ({ ...p, [mod]: false }));
    }
  }

  return (
    <Spin spinning={loading}>
      <Tabs size="small" activeKey={activeModule} onChange={setActiveModule}
        items={MODULE_TABS.map((t) => ({ key: t.key, label: t.label }))} />
      <div className="uss-field-header with-edit">
        <span /><span>Field</span>
        <span style={{ textAlign: 'center' }}>Visible</span>
        <span style={{ textAlign: 'center' }}>Editable</span>
        <span style={{ textAlign: 'center' }}>Mandatory</span>
      </div>
      <div className="uss-field-list">
        {fields.length === 0 && !loading && <div className="uss-empty">No fields found for this module.</div>}
        {fields.map((field, idx) => (
          <FieldRow key={field.fieldKey ?? idx} field={field} index={idx}
            onToggle={handleToggle} onReorder={handleReorder} />
        ))}
      </div>
      <div className="uss-modal-footer">
        <Button type="primary" size="small" loading={saving[activeModule]} onClick={handleSave}>
          Save {MODULE_TABS.find((t) => t.key === activeModule)?.label}
        </Button>
      </div>
    </Spin>
  );
}

export default function RolesSection() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [modalOpen, setModalOpen]       = useState(false);
  const [configType, setConfigType]     = useState('listView');
  const [roles, setRoles]               = useState([]);
  const [total, setTotal]               = useState(0);
  const [loading, setLoading]           = useState(false);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllRoles();
      setRoles(result.roles);
      setTotal(result.total);
    } catch {
      message.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  function openConfig(role, tab = 'listView') {
    setSelectedRole(role);
    setConfigType(tab);
    setModalOpen(true);
  }

  const fields = [
    { label: 'Role Name',   value: 'roleName',   width: 200 },
    { label: 'Description', value: 'description', width: 240 },
    { label: 'Users',       value: 'userCount',   width: 70, sorter: false },
    {
      label: 'Actions', value: '_configure', width: 200, sorter: false,
      render: (_, record) => (
        <Space size={2}>
          <Button size="small" type="link" onClick={() => openConfig(record)}>Configure</Button>
          <Button
            size="small"
            type="link"
            icon={<LockOutlined />}
            onClick={() => openConfig(record, 'permissions')}
          >
            Permissions
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <AppCard icon={<SafetyOutlined />} title="Roles"
        style={{ marginBottom: 0 }}
        bodyStyle={{ padding: 0 }}>
        <ParamListView
          listName="Roles"
          dataSource={roles}
          fields={fields}
          loading={loading}
          total={total}
        />
      </AppCard>

      <Modal
        className="uss-modal"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SafetyOutlined style={{ color: 'var(--color-brand)' }} />
            <span>Configure: {selectedRole?.roleName}</span>
          </div>
        }
        width={720} footer={null} destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <div className="uss-config-toggle">
            {CONFIG_TYPES.map((ct) => (
              <button key={ct.key}
                className={`uss-config-btn${configType === ct.key ? ' active' : ''}`}
                onClick={() => setConfigType(ct.key)}>{ct.label}</button>
            ))}
          </div>
        </div>
        {selectedRole && (
          <ModuleFieldConfig
            key={`${selectedRole?.roleId}-${configType}`}
            role={selectedRole}
            configType={configType}
          />
        )}
      </Modal>
    </>
  );
}